/**
 * Site-wide quiet-luxury motion and the ff-* section behaviour. Loaded on every page
 * by snippets/ff-motion.liquid.
 *
 * - Fade-up reveal for [data-ff-reveal] (IntersectionObserver, transform/opacity only).
 * - Auto-reveal across the theme: main sections after the first, product grid items, and
 *   resource cards get [data-ff-reveal] at runtime, including items inserted later
 *   (filtering, pagination). Skipped in the theme editor.
 * - Previous/next controls for [data-ff-slider] scroll-snap tracks.
 * - ARIA tabs for [data-ff-tabs] (click, arrow keys, Home/End).
 * - Pause/play for [data-ff-marquee].
 *
 * Progressive enhancement: content is fully visible without this file. The hidden state
 * only exists once `ff-js` is on <html>, and motion is skipped for reduced motion.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const designMode = Boolean(window.Shopify?.designMode);

/** Theme elements that get the reveal automatically. ff-* sections tag themselves. */
const AUTO_ITEM_SELECTOR = 'main .product-grid__item, main .resource-list__item';
const STAGGER_MS = 60;
const STAGGER_CAP = 5;

/** @type {IntersectionObserver | null} */
let revealObserver = null;

function getRevealObserver() {
  revealObserver ??= new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );
  return revealObserver;
}

/**
 * @param {Iterable<Element>} targets
 */
function observeReveal(targets) {
  const list = [...targets].filter((target) => !target.classList.contains('is-revealed'));
  if (list.length === 0) return;

  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    for (const target of list) target.classList.add('is-revealed');
    return;
  }

  // Anything already on screen stays visible, so enabling the hidden state never flickers.
  // Read every position first, then write: interleaving reads and class changes would force
  // a full layout per element on a large DOM.
  const viewportHeight = window.innerHeight;
  const onScreen = list.map((target) => target.getBoundingClientRect().top < viewportHeight);
  const observer = getRevealObserver();
  list.forEach((target, index) => {
    if (onScreen[index]) target.classList.add('is-revealed');
    else observer.observe(target);
  });

  document.documentElement.classList.add('ff-js');
}

/**
 * Tags theme sections and cards that do not opt in themselves.
 * @param {ParentNode} root
 * @returns {Element[]}
 */
function autoTag(root) {
  if (designMode) return [];
  /** @type {Element[]} */
  const tagged = [];

  const main = document.querySelector('main');
  if (main && root === document) {
    const sections = [...main.querySelectorAll(':scope > .shopify-section')];
    for (const section of sections.slice(1)) {
      if (section.querySelector('.ff-section, .product-grid__item, .resource-list__item')) continue;
      if (section.hasAttribute('data-ff-reveal')) continue;
      section.setAttribute('data-ff-reveal', '');
      tagged.push(section);
    }
  }

  const items = root instanceof Element && root.matches(AUTO_ITEM_SELECTOR) ? [root] : root.querySelectorAll(AUTO_ITEM_SELECTOR);
  for (const item of items) {
    if (item.hasAttribute('data-ff-reveal') || item.closest('[data-ff-reveal]:not(.is-revealed)')) continue;
    const siblings = item.parentElement ? [...item.parentElement.children] : [item];
    const index = Math.min(siblings.indexOf(item) % 4, STAGGER_CAP);
    item.setAttribute('data-ff-reveal', '');
    if (item instanceof HTMLElement) item.style.setProperty('--ff-delay', `${index * STAGGER_MS}ms`);
    tagged.push(item);
  }

  return tagged;
}

/**
 * @param {HTMLElement} slider
 */
function initSlider(slider) {
  if (slider.dataset.ffSliderReady) return;
  slider.dataset.ffSliderReady = 'true';

  // Review apps (Judge.me) render their own item list; it doubles as the track.
  const track = slider.querySelector('[data-ff-slider-track], .jdgm-carousel__item-wrapper');
  const prev = slider.querySelector('[data-ff-slider-prev]');
  const next = slider.querySelector('[data-ff-slider-next]');
  if (!(track instanceof HTMLElement) || !(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) {
    return;
  }

  const isRtl = () => getComputedStyle(track).direction === 'rtl';

  const update = () => {
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    prev.disabled = position <= 2;
    next.disabled = position >= max - 2;
    slider.toggleAttribute('data-ff-slider-static', max <= 2);
  };

  /** @param {1 | -1} direction */
  const go = (direction) => {
    const distance = track.clientWidth * 0.9 * direction * (isRtl() ? -1 : 1);
    track.scrollBy({ left: distance, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  };

  prev.addEventListener('click', () => go(-1));
  next.addEventListener('click', () => go(1));
  track.addEventListener('scroll', update, { passive: true });
  new ResizeObserver(update).observe(track);
  update();
}

/**
 * @param {HTMLElement} container
 */
function initTabs(container) {
  if (container.dataset.ffTabsReady) return;
  container.dataset.ffTabsReady = 'true';

  const tabs = [...container.querySelectorAll('[data-ff-tab]')].filter((tab) => tab instanceof HTMLButtonElement);
  if (tabs.length === 0) return;

  /** @param {HTMLButtonElement} tab @param {boolean} [focus] */
  const select = (tab, focus = false) => {
    for (const other of tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    }
    if (focus) tab.focus();
  };

  for (const tab of tabs) {
    tab.addEventListener('click', () => select(tab));
    tab.addEventListener('keydown', (event) => {
      const index = tabs.indexOf(tab);
      const rtl = getComputedStyle(container).direction === 'rtl';
      /** @type {number | null} */
      let target = null;
      if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) target = (index + 1) % tabs.length;
      if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) target = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = tabs.length - 1;
      if (target === null) return;
      event.preventDefault();
      const nextTab = tabs[target];
      if (nextTab) select(nextTab, true);
    });
  }

  // Theme editor: selecting a tab block shows its panel.
  container.addEventListener('shopify:block:select', (event) => {
    if (event.target instanceof HTMLButtonElement && tabs.includes(event.target)) select(event.target);
  });
}

/**
 * @param {HTMLElement} marquee
 */
function initMarquee(marquee) {
  if (marquee.dataset.ffMarqueeReady) return;
  marquee.dataset.ffMarqueeReady = 'true';

  const toggle = marquee.querySelector('[data-ff-marquee-toggle]');
  if (!(toggle instanceof HTMLButtonElement)) return;

  toggle.hidden = false;
  toggle.addEventListener('click', () => {
    const paused = marquee.toggleAttribute('data-ff-paused');
    toggle.setAttribute('aria-pressed', String(paused));
  });
}

/**
 * @param {ParentNode} root
 */
function init(root = document) {
  const autoTagged = autoTag(root);
  observeReveal([...root.querySelectorAll('[data-ff-reveal]'), ...autoTagged]);

  for (const slider of root.querySelectorAll('[data-ff-slider]')) {
    if (slider instanceof HTMLElement) initSlider(slider);
  }
  for (const tabs of root.querySelectorAll('[data-ff-tabs]')) {
    if (tabs instanceof HTMLElement) initTabs(tabs);
  }
  for (const marquee of root.querySelectorAll('[data-ff-marquee]')) {
    if (marquee instanceof HTMLElement) initMarquee(marquee);
  }
}

/**
 * Reveal grid items inserted after load (filters, pagination). Only product-grid pages need
 * this, and only the grid is watched, so app widgets injecting nodes elsewhere cost nothing.
 */
function watchInsertedItems() {
  if (designMode || reducedMotion.matches || !('MutationObserver' in window)) return;
  const grids = document.querySelectorAll('main .product-grid');
  if (grids.length === 0) return;

  const observer = new MutationObserver((mutations) => {
    /** @type {Element[]} */
    const added = [];
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element && node.matches(AUTO_ITEM_SELECTOR)) added.push(...autoTag(node));
      }
    }
    if (added.length > 0) observeReveal(added);
  });
  for (const grid of grids) observer.observe(grid, { childList: true });
}

init();
watchInsertedItems();

// Theme editor: re-initialise sections that are added or re-rendered.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
